<template>
  <v-main>

    <v-container
        id="dependent-container"
        fluid
        tag="section"
    >

        <base-material-card
            icon="mdi-family-tree"
            class="px-5 py-3"
            ref="form"
        >

          <div class="page-errors">
            <shared-page-messages :error-messages="errorMessages"
                                  :warning-messages="warningMessages"
                                  :success-messages="successMessages">
            </shared-page-messages>
          </div>

          <v-form ref="dependentForm"
                  v-model="valid"
                  @submit.prevent="validate"
                  lazy-validation
          >

            <v-row>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-select
                    v-model="dependent.relationType"
                    label="Relationship"
                    :items="validRelationTypes"
                    item-text="name"
                    item-value="id"
                    return-object
                    :rules="rules.required"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-text-field
                    v-model="dependent.firstName"
                    label="First Name"
                    :rules="rules.required"
                ></v-text-field>
              </v-col>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-text-field
                    v-model="dependent.middleName"
                    label="Middle Name/Initial (optional)"
                ></v-text-field>
              </v-col>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-text-field
                    v-model="dependent.lastName"
                    label="Last Name"
                    :rules="rules.required"
                ></v-text-field>
              </v-col>
            </v-row>


            <v-row>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-text-field
                    v-model="dependent.city"
                    label="City/Town"
                    :rules="rules.required"
                ></v-text-field>
              </v-col>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-text-field
                    v-model="dependent.county"
                    label="County"
                ></v-text-field>
              </v-col>
              <v-col
                  cols="12"
                  sm="4"
              >
                <v-select
                    label="Country"
                    v-model="computedCountry"
                    :items="countriesList"
                    item-text="name"
                    item-value="code"
                    return-object
                    :rules="rules.required"
                >
                </v-select>
              </v-col>
            </v-row>

            <v-row>

              <v-col
                  cols="12"
                  sm="4"
              >
                <v-text-field
                    v-model="dependent.birthYear"
                    label="Year of Birth (YYYY)"
                    :rules="rules.birthYear"
                ></v-text-field>
              </v-col>

              <v-col
                  cols="12"
                  sm="4"
              >
                <v-radio-group
                    v-model="dependent.gender"
                    mandatory
                    row
                >
                  <v-radio
                      v-for="(item, i) in genderOptions"
                      :key="i"
                      :label="item"
                      :value="item"
                  >
                  </v-radio>

                </v-radio-group>
              </v-col>

              <v-col
                  cols="12"
                  sm="4"
              >
                <v-checkbox
                    v-model="dependent.requestDelete"
                    label="Request Delete">
                </v-checkbox>
              </v-col>
            </v-row>

            <v-row>
               <v-col
                  cols="6"
                  sm="4"
              >
                <v-select
                    v-model="updateReason"
                    label="Reason for Change"
                    :items="updateReasonOptions"
                    :rules="rules.required"
                ></v-select>
              </v-col>

              <v-col
                  cols="12"
                  sm="4"
                  v-if="updateReason === 'Other'"
              >
                <v-text-field
                    v-model="otherUpdateReason"
                    label="Other Reason for Change"
                    :rules="rules.required"
                ></v-text-field>
              </v-col>


            </v-row>

            <v-row>
              <v-col
                  cols="6"
                  sm="4"
              >
                <v-select
                    v-model="dependent.status"
                    label="Status"
                    :items="computedStatusItems"
                    return-object
                    :rules="rules.required"
                ></v-select>
              </v-col>

            </v-row>


            <v-row>
              <v-col
                  cols="12"
                  sm="4"
              >
                <div class="my-2">
                  <v-btn
                      color="primary"
                      dark
                      type="submit"
                  >
                    Submit
                  </v-btn>
                  <v-btn
                      type="button"
                      color="red"
                      dark
                      @click="confirmDeleteDependent"
                      v-if="dependent.id"
                  >
                    Delete
                  </v-btn>
                </div>
              </v-col>
            </v-row>

            <v-dialog
                v-model="showDeleteDialog"
                max-width="400"
            >
              <template>
                <v-card>
                  <v-card-text>
                    Are you sure you want to delete this dependent?
                  </v-card-text>
                  <v-card-actions  class="justify-center">
                    <v-btn
                        type="button"
                        color="primary"
                        dark
                        @click="showDeleteDialog = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                        type="button"
                        color="red"
                        dark
                        @click="deleteDependent"
                    >
                      Delete
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </template>
            </v-dialog>

          </v-form>


        </base-material-card>

      <v-expansion-panels
          multiple
          light
          hover
          v-model="expandedPanels"
          v-if="dependent.id"
      >
        <v-expansion-panel>
          <v-expansion-panel-header>Audit Trail</v-expansion-panel-header>

          <v-expansion-panel-content>
            <shared-audit-trail-view :item-id="dependent.id"></shared-audit-trail-view>
          </v-expansion-panel-content>

        </v-expansion-panel>
      </v-expansion-panels>

    </v-container>

  </v-main>
</template>

<script>
  import api from '@/services/api'
  import validation from "@/utils/validation"
  import geoUtils from "@/utils/geography"
  import constants from "@/utils/constants"
  import SharedAuditTrailView from '../shared/AuditTrailView'
  import depUtils from '@/utils/dependents'
  import SharedPageMessages from '../shared/PageMessages.vue'

  export default {
    name: 'CoreDependentView',

    components: {
      SharedPageMessages,
      SharedAuditTrailView
    },

    async created() {
      this.userId = this.$route.params.memberId
      this.dependentId = this.$route.params.dependentId
      this.statesList = geoUtils.fetchUsStates()
      this.countriesList = geoUtils.fetchCountries()

      await this.fetchUser()
      await this.fetchRelationTypes()
      if (this.dependentId) {
        await this.fetchDependent()
      }
      await this.fetchDependents()
      this.buildRelationTypeOptions()
    },

    computed: {
      computedStatusItems: {
        get: function () {
          return Object.keys(this.dependentStatusMap)
              .map((key) => ({value: key, text: this.dependentStatusMap[key]}))
        }
      },
      computedCountry: {
        get() {
          return geoUtils.findCountryByCode(this.dependent.country)
        },
        set(value) {
          this.dependent.country = value.code
        }
      }
    },

    watch: {
      relationTypes: {
        handler() {
          this.buildRelationTypeOptions()
        },
        deep: true
      },
      dependents: {
        handler() {
          this.buildRelationTypeOptions()
        },
        deep: true
      }
    },

    methods: {
      clearMessages() {
        this.errorMessages = []
        this.successMessages = []
        this.warningMessages = []
      },
      validate() {
        this.clearMessages()
        if(this.$refs.dependentForm.validate()) {
          if (this.dependent.id !== undefined) {
            return this.updateDependent()
          }
          this.addDependent()
        }
      },
      confirmDeleteDependent() {
        this.clearMessages()
        if(this.$refs.dependentForm.validate("comment")) {
          this.showDeleteDialog = true
        }
      },
      buildRelationTypeOptions() {
        let counts = depUtils.countDependentsByRelation(this.dependents)
        if (this.dependent.relationType !== undefined && this.dependent.relationType.id !== undefined) {
          counts[this.dependent.relationType.id] -= 1
        }
        this.validRelationTypes = this.relationTypes.filter(el => {
          return (el.max == null) || (counts[el.id] === undefined) || (counts[el.id] < el.max)
        })
      },
      fetchRelationTypes() {
        return api
            .fetchRelationTypes()
            .then(response => {
              this.relationTypes = response.data
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      fetchUser() {
        return api
            .fetchUser(this.userId)
            .then(response => {
              this.user = response.data
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      fetchDependent() {
        return api
            .fetchDependent(this.userId, this.dependentId)
            .then(response => {
              this.dependent = response.data
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      fetchDependents() {
        return api
            .fetchDependents(this.userId)
            .then(response => {
              this.dependents = response.data
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      addDependent() {
        this.$gtag.event('admin_add_dependent')

        let payload = this.dependent
        payload.comment = this.otherUpdateReason || this.updateReason
        payload.relationTypeId = payload.relationType.id
        payload.status = payload.status.value
        api
            .addDependent(this.userId, payload)
            .then(response => {
              if (response.status === 201) {
                this.$router.push(`/member/${this.userId}/view`)
              }
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      updateDependent() {
        this.$gtag.event('admin_update_dependent')
        this.clearMessages()

        let payload = this.dependent
        payload.comment = this.otherUpdateReason || this.updateReason
        payload.relationTypeId = payload.relationType.id
        payload.status = payload.status.value
        api
            .updateDependent(this.userId, this.dependentId, payload)
            .then(response => {
              if (response.status === 200) {
                this.$router.push(`/member/${this.userId}/view`)
              }
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      deleteDependent() {
        this.$gtag.event('admin_delete_dependent')

        this.clearMessages()
        let payload = {
          comment: this.otherUpdateReason || this.updateReason
        }
        api.deleteDependent(this.userId, this.dependentId, payload)
            .then(response => {
              if (response.status === 204) {
                this.$router.push(`/member/${this.userId}/view`)
              }
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      handleError(err) {
        let errorMessage = api.parseResponseError(err)
        this.errorMessages.push(errorMessage)
      }
    },

    data() {
      return {
        valid: true,
        errorMessages: [],
        successMessages: [],
        warningMessages: [],
        userId: null,
        dependentId: null,
        user: {},
        dependent: {},
        updateReason: null,
        otherUpdateReason: null,
        showDeleteDialog: false,
        genderOptions: [
          'Male',
          'Female',
          'Other'
        ],
        updateReasonOptions: [
            'Marriage',
            'Birth or Legal Adoption',
            'Divorce',
            'Death',
            'Other'
        ],
        relationTypes: [],
        validRelationTypes: [],
        countriesList: [],
        expandedPanels: [0],
        auditLogs: [],
        dependents: [],
        dependentCounts: {},
        dependentStatusMap: constants.getDependentStatusMap(),
        rules: {
          required: [
            v => !!v || 'required'
          ],
          numeric: [
            v => validation.isInteger(v) || 'must be a number'
          ],
          birthYear: [
            v => validation.isInteger(v) || 'must be a year number',
            v => validation.isMoreThan(v, 1900) || 'must be after 1900',
            v => validation.isBeforeOrEqualToCurrentYear(v) || 'must be before or equal to current year'
          ]
        }
      }
    }
  }
</script>

<style lang="sass">
.v-expansion-panel-header
  font-size: 1.15em
th
  vertical-align: top

</style>
